import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPostStream } from '../../lib/api-client';
import { enumLabel } from '../../lib/labels';
import { formatMoney } from '../../lib/money';
import { apiError } from '../../lib/utils';
import { defaultSender, type CompanyMailbox } from '../../lib/company-mail';
import { CREDIT_NOTE_EMAIL, renderEmailTemplate } from '../../lib/email-template';
import { EmailTemplateSelect } from './TemplateSelects';
import { EmailFields } from './EmailFields';
import { EmailPreview } from './EmailPreview';
import { InvoiceFormat2 } from './InvoiceFormat2';
import { InvoicePreview } from './InvoicePreview';
import { Alert } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useActivity } from '../ui/Activity';
import { useNotice } from '../ui/Notice';
import { SectionCard, StatusPill } from '../ui/chrome';

export interface CreditNoteRecord {
  id: string;
  number: string;
  status: string;
  currency: string;
  netPence: number;
  vatPence: number;
  issuedOn?: string;
  emailSubject?: string;
  emailBody?: string;
  sentTo?: string[];
  sentAt?: string;
  sharePointUrl?: string;
  lines: { description: string; amountPence: number }[];
}

export interface CreditNoteInvoice {
  id: string;
  number?: string;
  internalNumber?: string;
  customerId?: string;
  customerName?: string;
  layout?: string;
  currency?: string;
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  billToDetails?: string;
  bankLines?: [string, string][];
  contactName?: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export function CreditNotePane({ invoice, note, onIssued }: { invoice: CreditNoteInvoice; note: CreditNoteRecord; onIssued: () => void }) {
  const notice = useNotice();
  const activity = useActivity();
  const [from, setFrom] = useState<CompanyMailbox>(defaultSender('invoice'));
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const invoiceNumber = invoice.internalNumber || invoice.number || 'invoice';

  useEffect(() => {
    let cancelled = false;
    const values = { number: note.number, customer: invoice.customerName || '', title: invoiceNumber };
    setSubject(note.emailSubject || renderEmailTemplate(CREDIT_NOTE_EMAIL.subject, values));
    setBody(note.emailBody || renderEmailTemplate(CREDIT_NOTE_EMAIL.body, values));
    void apiGet<{ id: string; billingEmails?: string[] }[]>('/api/customers').then((customers) => {
      if (cancelled) return;
      const customer = customers.find((item) => item.id === invoice.customerId);
      const emails = note.sentTo?.length ? note.sentTo : [invoice.billToEmail || '', ...(customer?.billingEmails || [])];
      setTo(emails.map((email) => email.trim()).filter((email, index, all) => email && all.indexOf(email) === index));
    }).catch((err) => { if (!cancelled) setError(apiError(err, 'Could not open the credit note')); });
    return () => { cancelled = true; };
  }, [note.id, note.emailSubject, note.emailBody, note.number, note.sentTo, invoice.customerId, invoice.customerName, invoice.billToEmail, invoiceNumber]);

  async function issue(send: boolean) {
    const payload = { send, to, cc, from, emailSubject: subject, emailBody: body };
    if (!send) {
      const saved = await notice.run('Issuing the credit note', () => apiPost(`/api/credit-notes/${note.id}/issue`, payload), 'Marked issued. No email was sent.');
      if (saved !== undefined) onIssued();
      return;
    }
    const sent = await activity.track({
      title: 'Sending your credit note',
      subtitle: subject || note.number,
      dockTitle: `Sending credit note ${note.number}`,
      to: to.join(', '),
      attachments: note.number,
      kindLabel: 'Credit note',
      steps: [
        { id: 'documents', label: 'Documents prepared', detail: note.number },
        { id: 'issued', label: 'Credit note issued' },
        { id: 'email', label: 'Sending email', detail: 'Waiting for the mail service to confirm.' },
        { id: 'sharepoint', label: 'Storing the credit note file' },
      ],
    }, (apply, signal) => apiPostStream(`/api/credit-notes/${note.id}/issue`, { ...payload, progress: true }, apply, {}, signal));
    if (sent) onIssued();
  }

  const issued = note.status === 'issued';
  const currency = note.currency || invoice.currency || 'GBP';
  const lines = (note.lines || []).map((line) => ({ qty: 1, description: line.description, unitPence: line.amountPence, amountPence: line.amountPence }));
  const preview = invoice.layout === '1'
    ? <InvoicePreview documentTitle="CREDIT NOTE" numberLabel="CREDIT NOTE NO." number={note.number} customerName={invoice.customerName || ''} billingEmails={to} billToName={invoice.billToName || ''} billToAddress={invoice.billToAddress || ''} billToEmail={invoice.billToEmail || ''} billToDetails={invoice.billToDetails || ''} bankLines={invoice.bankLines} title={`Credit for ${invoiceNumber}`} issuedOn={note.issuedOn || ''} dueOn="" currency={currency} poNumber="" contactName={invoice.contactName || ''} contactRole={invoice.contactRole || ''} contactEmail={invoice.contactEmail || ''} contactPhone={invoice.contactPhone || ''} notice="" lines={lines} vatPence={note.vatPence} />
    : <InvoiceFormat2 documentTitle="CREDIT NOTE" number={note.number} customerName={invoice.customerName || ''} billingEmails={to} billToName={invoice.billToName || ''} billToAddress={invoice.billToAddress || ''} billToEmail={invoice.billToEmail || ''} billToDetails={invoice.billToDetails || ''} bankLines={invoice.bankLines} title={`Credit for ${invoiceNumber}`} issuedOn={note.issuedOn || ''} dueOn="" currency={currency} poNumber="" contactName={invoice.contactName || ''} contactRole={invoice.contactRole || ''} contactEmail={invoice.contactEmail || ''} contactPhone={invoice.contactPhone || ''} notice="" lines={lines} vatPence={note.vatPence} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill value={note.status} />
        <span className="text-sm text-[var(--bo-muted)]">{note.number}{note.issuedOn ? ` · ${note.issuedOn}` : ''}</span>
        <span className="ml-auto flex gap-2">
          <Button variant="outline" disabled={issued} onClick={() => void issue(false)}>Issue without email</Button>
          <Button onClick={() => void issue(true)}>{issued ? 'Send' : 'Issue & send'}</Button>
        </span>
      </div>
      {error ? <Alert error>{error}</Alert> : null}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <SectionCard title="Credit note" hint="This credit is for this invoice. It is stored and emailed from here. It is not sent to Crunch.">
            <div className="space-y-2 text-sm">
              {(note.lines || []).map((line, index) => (
                <div key={`${line.description}-${index}`} className="flex justify-between gap-3">
                  <span>{line.description || 'Line'}</span>
                  <span className="tabular-nums">{formatMoney(line.amountPence, currency)}</span>
                </div>
              ))}
              <div className="border-t border-[var(--bo-border)] pt-2 text-right text-gray-500">Net {formatMoney(note.netPence, currency)} · VAT {formatMoney(note.vatPence, currency)}</div>
              <div className="text-right text-2xl">{formatMoney(note.netPence + note.vatPence, currency)}</div>
              <p>{enumLabel(note.status)}{note.sentAt ? ` · Sent ${note.sentAt.slice(0, 10)}` : ''}</p>
              {note.sharePointUrl ? <a className="text-[var(--bo-link)]" href={note.sharePointUrl} target="_blank" rel="noreferrer">File</a> : null}
            </div>
          </SectionCard>
          <SectionCard title="Email" hint="The message sent with the credit note.">
            <EmailTemplateSelect kind="credit_note" values={{ number: note.number, customer: invoice.customerName || '', title: invoiceNumber }} onApply={(nextSubject, nextBody) => { setSubject(nextSubject); setBody(nextBody); }} />
            <EmailFields from={from} to={to} cc={cc} subject={subject} body={body} onFrom={setFrom} onTo={setTo} onCc={setCc} onSubject={setSubject} onBody={setBody} />
          </SectionCard>
          <EmailPreview from={from} recipients={to.join(', ')} cc={cc.join(', ')} subject={subject} body={body} senderName={invoice.contactName} senderTitle={invoice.contactRole} senderEmail={invoice.contactEmail} senderPhone={invoice.contactPhone} />
        </div>
        <div className="min-h-0 overflow-y-auto rounded-lg bg-[var(--bo-preview)] p-3">
          <div className="mb-3"><Button variant="outline" onClick={() => window.open(`/api/credit-notes/${note.id}/pdf`, '_blank')}>Download</Button></div>
          {preview}
        </div>
      </div>
    </div>
  );
}
